import './EventItem.css';

import LightModeIcon from '@mui/icons-material/LightMode';
import ChurchIcon from '@mui/icons-material/Church';
import HomeIcon from '@mui/icons-material/Home';
import FavoriteIcon from '@mui/icons-material/Favorite';
import KayakingIcon from '@mui/icons-material/Kayaking';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import HandymanIcon from '@mui/icons-material/Handyman';
import AssistantPhotoIcon from '@mui/icons-material/AssistantPhoto';
import ArticleIcon from '@mui/icons-material/Article';
import LocationPinIcon from '@mui/icons-material/LocationPin';
import SubjectIcon from '@mui/icons-material/Subject';
import PostAddIcon from '@mui/icons-material/PostAdd';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckIcon from '@mui/icons-material/Check';
import CastleIcon from '@mui/icons-material/Castle';

import { Box, Collapse, ListItem, ListItemIcon, Typography, Divider, Link, ListItemText, List, Badge, CircularProgress } from "@mui/material";
import { useMemo, useState, useSyncExternalStore } from "react";
import { useQuery } from '@tanstack/react-query';
import { differenceInYears, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { grey } from "@mui/material/colors";
import MuiMarkdown from "mui-markdown";
import { lcFirst, frenchEvents, type GrampsEventMedia } from "../utils";
import { getGrampsBackendBase, isMarchandViveretHost, readBearerToken, subscribeToBearerToken } from '../grampsAuth';
import { useViewRoute } from '../viewRoute';
import {
    citationExtended,
    fetchCitationsByHandles,
    fetchSourcesByHandles,
    handlesDigest,
    restDateToJsDate,
    restEventDateString,
    restEventRepublicanDateString,
    restEventParticipantsWithRole,
    restPersonGivenName,
    restPersonIsMale,
    restPersonName,
    restPlaceName,
    toGrampsNote,
    restEventPrincipals,
    type EventParticipant,
    type RestCitation,
    type RestEvent,
    type RestNote,
    type RestPerson,
    type RestPlace,
    type RestSource,
} from '../api/grampsRest';
import { getMathMarkdownOptions } from './markdownMath';
import { MediaList } from './MediaList';
import { NoteItem } from './NoteItem';
import { useEventPeople } from '../hooks/useMediaOcr';
import type { ActPerson } from '../api/ocr';

function relatedLinkWord(who: 'child' | 'own', related: RestPerson): string {
    const isMale = restPersonIsMale(related);
    if (who === 'child') {
        return isMale ? 'son fils' : 'sa fille';
    }
    return isMale ? 'son époux' : 'son épouse';
}

export interface RestEventCitation {
    citation: RestCitation;
    source: RestSource | undefined;
    notes: RestNote[];
}

/**
 * Resolves an event's citations (and their sources) only once the caller asks for
 * them — the details panel is collapsed by default, so on a person with many events
 * this keeps two round trips per view off the initial load.
 */
function useEventCitations(event: RestEvent, enabled: boolean): { citations: RestEventCitation[]; isLoading: boolean } {
    const { treeName } = useViewRoute();
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;

    const handles = useMemo(() => {
        const list = event.citation_list ?? [];
        return { list, digest: handlesDigest(list) };
    }, [event]);

    const citationsQuery = useQuery({
        queryKey: ['citations', treeName, handles.digest],
        queryFn: () => fetchCitationsByHandles(handles.list, token),
        enabled: enabled && handles.list.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    // `extend=source_handle` normally fills this in; the fetch below is the fallback
    // for citations where it did not.
    const missingSources = useMemo(() => {
        const list = [...new Set((citationsQuery.data ?? [])
            .filter((citation) => !citationExtended(citation).source && citation.source_handle)
            .map((citation) => citation.source_handle!))];
        return { list, digest: handlesDigest(list) };
    }, [citationsQuery.data]);

    const sourcesQuery = useQuery({
        queryKey: ['sources', treeName, missingSources.digest],
        queryFn: () => fetchSourcesByHandles(missingSources.list, token),
        enabled: enabled && missingSources.list.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    const citations = useMemo(() => {
        const sourcesByHandle = new Map((sourcesQuery.data ?? [])
            .filter((source) => source.handle)
            .map((source) => [source.handle!, source]));
        return (citationsQuery.data ?? []).map((citation) => {
            const extension = citationExtended(citation);
            return {
                citation,
                source: extension.source ?? (citation.source_handle ? sourcesByHandle.get(citation.source_handle) : undefined),
                notes: extension.notes ?? [],
            };
        });
    }, [citationsQuery.data, sourcesQuery.data]);

    return { citations, isLoading: citationsQuery.isFetching || sourcesQuery.isFetching };
}

function placeCoordinates(place?: RestPlace): [number, number] | undefined {
    const longitude = Number.parseFloat(place?.long ?? '');
    const latitude = Number.parseFloat(place?.lat ?? '');
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return undefined;
    return longitude >= -180 && longitude <= 180 && latitude >= -90 && latitude <= 90 ? [longitude, latitude] : undefined;
}

// Events recorded in a register act: the only ones the links machine reads.
const ACT_EVENT_TYPES = new Set([
    'Birth', 'Baptism', 'Christening', 'Death', 'Burial', 'Marriage',
    'Marriage Banns', 'Marriage Contract',
]);

const ACT_ROLES: Record<ActPerson['role'], 'Witness' | 'Informant' | 'Godparent'> = {
    witness: 'Witness',
    informant: 'Informant',
    godparent: 'Godparent',
};

/** What the act says of someone besides their name: "cultivateur, 71 ans". */
function actDetails(person: ActPerson | undefined): string {
    if (!person) return '';
    return [
        person.occupation?.trim(),
        person.age ? `${person.age} ans` : '',
    ].filter(Boolean).join(', ');
}

/** Why someone the act names is shown apart from the people of the tree. */
function outsideNote(person: ActPerson): string {
    return person.outcome === 'unique' || person.outcome === 'tied'
        ? 'D’après l’acte : un rapprochement avec l’arbre attend dans l’atelier'
        : 'D’après l’acte, pas dans l’arbre';
}

/** One line of attendees: the people of the tree, then those the act names
 * who are not in it. */
interface ParticipantLine {
    singular: string;
    plural: string;
    people: EventParticipant[];
    outside: ActPerson[];
}

export function RestEventItem({
    event,
    who,
    isFamily,
    relatedPerson,
    relatedLabel,
    personBirthDate,
    place,
    media,
    notes,
    onPlaceClick,
    principals,
    viewerHandle,
}: {
    event: RestEvent;
    who: 'own' | 'child';
    isFamily: boolean;
    relatedPerson?: RestPerson;
    relatedLabel?: string;
    personBirthDate?: Date;
    place?: RestPlace;
    media: GrampsEventMedia[];
    notes: RestNote[];
    onPlaceClick?: (coordinates: [number, number]) => void;
    /** Set for an event the person only witnessed: whom it was about, with the
     * relation when it is known. */
    principals?: { person: EventParticipant; relation?: string }[];
    /** The person whose drawer this is: not repeated among the witnesses. */
    viewerHandle?: string;
}) {
    const { setSideItemId, treeName } = useViewRoute();
    const [detailsOpen, setDetailsOpen] = useState(false);
    const actPeople = useEventPeople(treeName, event.handle ?? undefined, ACT_EVENT_TYPES.has(event.type ?? ''));
    const { citations, isLoading: citationsLoading } = useEventCitations(event, detailsOpen);

    const eventDate = restDateToJsDate(event.date);
    // Who attended without the event being about them, by role; the person whose
    // drawer this is is not repeated.
    const attending = (role: 'Witness' | 'Informant' | 'Godparent') =>
        restEventParticipantsWithRole(event, role).filter((person) => person.handle !== viewerHandle);
    const godparents = attending('Godparent');

    // What the acts say of the people they name. Someone the links machine
    // found among those linked to the event is the tree's; anyone else is
    // shown as the act names them. Checked against the event as it is now,
    // so a suggestion accepted since the last run does not show them twice.
    const linkedHandles = new Set([
        ...(['Witness', 'Informant', 'Godparent'] as const)
            .flatMap((role) => restEventParticipantsWithRole(event, role)),
        ...restEventPrincipals(event),
    ].map((person) => person.handle));
    const actPersonOf = (handle: string) => actPeople.find((person) => person.candidates.includes(handle));
    const outside = (role: 'Witness' | 'Informant' | 'Godparent') => actPeople.filter((person) =>
        ACT_ROLES[person.role] === role && !person.candidates.some((handle) => linkedHandles.has(handle)));
    const outsideGodparents = outside('Godparent');
    const participantLines: ParticipantLine[] = [
        { singular: 'Témoin', plural: 'Témoins', people: attending('Witness'), outside: outside('Witness') },
        { singular: 'Déclarant', plural: 'Déclarants', people: attending('Informant'), outside: outside('Informant') },
        {
            singular: 'Parrain', plural: 'Parrains',
            people: godparents.filter((person) => person.sex === 'M'),
            outside: outsideGodparents.filter((person) => person.sex === 'M'),
        },
        {
            singular: 'Marraine', plural: 'Marraines',
            people: godparents.filter((person) => person.sex === 'F'),
            outside: outsideGodparents.filter((person) => person.sex === 'F'),
        },
        {
            singular: 'Parrain ou marraine', plural: 'Parrains ou marraines',
            people: godparents.filter((person) => person.sex !== 'M' && person.sex !== 'F'),
            outside: outsideGodparents.filter((person) => person.sex !== 'M' && person.sex !== 'F'),
        },
    ].filter((line) => line.people.length + line.outside.length > 0);
    const type = event.type ?? '';

    const lastEdited = formatDistanceToNow(new Date((event.change ?? 0) * 1000), { locale: fr });

    const toggleDetails = () => setDetailsOpen(!detailsOpen);

    const eventId = String(event.gramps_id || '').trim();

    const backendEventUrl = (() => {
        if (!eventId || isMarchandViveretHost()) return '';
        const backendBase = getGrampsBackendBase();
        return `${backendBase.replace(/\/+$/, '')}/event/${encodeURIComponent(eventId)}`;
    })();

    return (<>
        <ListItem key={event.handle} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => toggleDetails()}>
            <Box sx={{ position: 'absolute', bottom: 6, right: 11, cursor: 'pointer' }} onClick={(e) => e.stopPropagation()}>
                {notes.length ? <Badge badgeContent={notes.length} color="secondary" onClick={() => toggleDetails()}>
                    <KeyboardArrowDownIcon fontSize="small" />
                </Badge> : !isMarchandViveretHost() && <KeyboardArrowDownIcon fontSize="small" onClick={() => toggleDetails()} />
                }
            </Box>
            <ListItemIcon>
                {type === 'Birth' ? <LightModeIcon sx={{ color: '#f6c84a' }} /> :
                    type === 'Marriage' ? <FavoriteIcon color='error' /> :
                        type === 'Death' ? <KayakingIcon sx={{ color: 'black' }} /> :
                            type === 'Christening' ? <ChurchIcon /> :
                                type === 'Residence' ? <HomeIcon /> :
                                    type === 'Military Service' ? <AssistantPhotoIcon /> :
                                        type === 'Award' ? <MilitaryTechIcon /> :
                                            type === 'Marriage Banns' ? <ArticleIcon /> :
                                                type === 'Census' ? <LocationPinIcon /> :
                                                    type === 'Will' ? <PostAddIcon /> :
                                                        type === 'Burial' ? <CheckBoxOutlineBlankIcon sx={{ color: 'black' }} /> :
                                                            type === 'Nobility Title' ? <CastleIcon sx={{ color: '#b8860b' }} /> :
                                                                <HandymanIcon />
                }
            </ListItemIcon>
            <Box>
                <Typography sx={type === 'Death' ? { color: grey[600] } : undefined}>
                    <strong title={restEventRepublicanDateString(event.date)}>{restEventDateString(event.date)} </strong>
                    {(who === 'own' && type === 'Birth' && !principals) || !eventDate || !personBirthDate ? <></> : <>à {differenceInYears(eventDate, personBirthDate)}&nbsp;ans :&nbsp;</>}
                    {type !== 'Occupation' ? frenchEvents[type as keyof typeof frenchEvents] ?? type : ''}
                    {principals && principals.length > 0 && <>
                        {' de '}
                        {principals.map(({ person, relation }, index) => (
                            <span key={person.handle}>
                                {index > 0 && (index === principals.length - 1 ? ' et ' : ', ')}
                                {relation && `${relation} `}
                                {person.grampsId
                                    ? <Link onClick={(e) => { e.stopPropagation(); setSideItemId(person.grampsId!); }} style={{ cursor: 'pointer' }}>{person.name}</Link>
                                    : person.name}
                            </span>
                        ))}
                    </>}
                    {(who === 'child' || isFamily) && relatedPerson
                        ? <>
                            {relatedLabel
                                ? ` de ${relatedLabel}`
                                : isFamily && (type === 'Marriage' || type === 'Marriage Contract' || type === 'Marriage Banns' || type === 'Residence' || type === 'Census')
                                    ? ' avec'
                                    : ` de ${relatedLinkWord(who, relatedPerson)}`}&nbsp;
                            <Link onClick={(e) => { e.stopPropagation(); if (relatedPerson.gramps_id) setSideItemId(relatedPerson.gramps_id); }} style={{ "cursor": "pointer" }}>{who === 'child' ? restPersonGivenName(relatedPerson) : restPersonName(relatedPerson)}</Link>
                        </>
                        : <></>
                    }
                    {event.description
                        ? type === 'Occupation'
                            ? <strong>{event.description}</strong>
                            : <><br />{lcFirst(event.description)}</>
                        : <></>
                    }
                </Typography>
                {participantLines.map(({ singular, plural, people, outside: named }) => (
                    <Typography key={singular} variant="body2" sx={{ color: 'text.secondary' }}>
                        {people.length + named.length > 1 ? plural : singular}&nbsp;:{' '}
                        {people.map((person, index) => {
                            const details = actDetails(actPersonOf(person.handle));
                            return (
                                <span key={person.handle}>
                                    {index > 0 && ' · '}
                                    {person.grampsId
                                        ? <Link onClick={(e) => { e.stopPropagation(); setSideItemId(person.grampsId!); }} style={{ cursor: 'pointer' }}>{person.name}</Link>
                                        : person.name}
                                    {details && `, ${details}`}
                                </span>
                            );
                        })}
                        {named.map((person, index) => {
                            const details = actDetails(person);
                            return (
                                <span
                                    key={`${person.written_name}-${index}`}
                                    title={[outsideNote(person), person.residence && `demeurant ${person.residence}`, person.quote && `« ${person.quote} »`].filter(Boolean).join('\n')}
                                    style={{ fontStyle: 'italic' }}
                                >
                                    {(people.length > 0 || index > 0) && ' · '}
                                    {person.written_name}
                                    {details && `, ${details}`}
                                </span>
                            );
                        })}
                    </Typography>
                ))}
                {place && (() => {
                    const coordinates = placeCoordinates(place);
                    return coordinates && onPlaceClick
                        ? <Typography variant="body2" className="event-location" onClick={(e) => { e.stopPropagation(); onPlaceClick(coordinates); }} sx={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>{restPlaceName(place)}</Typography>
                        : <Typography variant="body2" className="event-location">{restPlaceName(place)}</Typography>;
                })()}
            </Box>
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'contents' }}>
                <MediaList medias={media} thumbSizePx={55} withLabel />
            </Box>
        </ListItem>
        <Collapse in={detailsOpen} timeout={180} unmountOnExit>
            <ListItem sx={{ backgroundColor: grey[100], maxHeight: 450, overflowY: 'auto', alignItems: 'flex-start', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <ListItemIcon><SubjectIcon /></ListItemIcon>
                <ListItemText><small>
                    {backendEventUrl && <><Link href={backendEventUrl} target="_blank" rel="noopener noreferrer">{eventId}</Link>{' '}</>}édité il y a {lastEdited}

                    <List className="eventnotes">
                        {notes.map((note) => <NoteItem key={note.handle} note={toGrampsNote(note)} />)}
                    </List>
                    <List className="eventcitations">
                        {citationsLoading && citations.length === 0 && <ListItem sx={{ py: 0.5 }}><CircularProgress size={14} /></ListItem>}
                        {citations.map(({ citation, source, notes: citationNotes }) => {
                            const trust = citation.confidence ?? 0;
                            return (
                                <ListItem key={citation.handle} sx={{ padding: 0, margin: 0 }}>
                                    <ListItemIcon sx={{
                                        color: trust === 0 ? 'red' : trust <= 2 ? 'orange' : 'green'
                                    }}><CheckIcon fontSize="small"></CheckIcon></ListItemIcon>
                                    <ListItemText>
                                        <small>
                                            {source?.title} {source?.author ? ', ' : ''}<i>{source?.author}</i>
                                            {citation.page ? ` (${citation.page})` : ''}
                                        </small>
                                        {citationNotes.length ? <List className="eventcitationnote">
                                            {citationNotes.map((note) => (
                                                <ListItem key={note.handle}>
                                                    <ListItemText>
                                                        <MuiMarkdown options={getMathMarkdownOptions()}>{note.text?.string ?? ''}</MuiMarkdown>
                                                    </ListItemText>
                                                </ListItem>))
                                            }
                                        </List> : <></>}
                                    </ListItemText>
                                </ListItem>)
                        })
                        }
                    </List>
                </small>
                </ListItemText>
            </ListItem>
        </Collapse>
        <Divider />
    </>)
}
